import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ResourceStatus } from 'libs.library_manager';
import { Table, useLocalizeMessage, Toggle, Button, Text } from 'libs.nucleus';
import { PageHeader, useBooleanState } from 'libs.react';
import { DEFAULT_DEBOUNCE_TIME } from 'libs.react_internal';
import { useDashboardsColumns } from './dashboards_columns.hook';
import { useDashboards } from '../../hooks';
import { CreateDashboardModal } from '../create_dashboard_modal/create_dashboard_modal.component';
export const DashboardsList = () => {
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const [isCreateModalOpen, openCreateModal, closeCreateModal] = useBooleanState();
    const [showArchived, setShowArchived] = useState(false);
    const [statusFilter, setStatusFilter] = useState();
    const [searchValue, setSearchValue] = useState('');
    const { dashboards, fetchDashboards, archiveDashboard, isLoading } = useDashboards();
    const handleArchiveDashboard = useCallback(async (dashboard) => {
        try {
            await archiveDashboard(dashboard.id);
            await fetchDashboards(showArchived, statusFilter, searchValue);
        }
        catch {
            // Error already handled in hook
        }
    }, [archiveDashboard, fetchDashboards, showArchived, statusFilter, searchValue]);
    const dashboardsColumns = useDashboardsColumns({
        archiveDashboard: handleArchiveDashboard,
    });
    useEffect(() => {
        let isMounted = true;
        const loadDashboards = async () => {
            try {
                await fetchDashboards(showArchived, statusFilter, searchValue);
            }
            catch {
                // Error already handled in hook
            }
        };
        if (isMounted) {
            loadDashboards();
        }
        return () => {
            isMounted = false;
        };
    }, [fetchDashboards, showArchived, statusFilter, searchValue]);
    const statusFilterFields = useMemo(() => [
        {
            fieldLabel: translate('Status'),
            fieldName: 'status',
            fieldKey: 'status',
            fieldWidth: 'sm',
            filterOptions: [
                { label: translate('In progress'), value: ResourceStatus.DRAFT },
                { label: translate('In review'), value: ResourceStatus.INREVIEW },
                { label: translate('Ready for use'), value: ResourceStatus.PUBLISHED },
            ],
        },
    ], [translate]);
    const handleStatusFilterChange = useCallback((filters) => {
        const selectedStatus = filters.status;
        if (selectedStatus && 'value' in selectedStatus) {
            setStatusFilter(selectedStatus.value);
            return;
        }
        setStatusFilter(undefined);
    }, []);
    const debouncedSearchChange = useMemo(() => {
        let timeoutId;
        return (value) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setSearchValue(value);
            }, DEFAULT_DEBOUNCE_TIME);
        };
    }, []);
    const handleCreateSuccess = useCallback((dashboard) => {
        navigate(`/library/dashboards/${dashboard.id}`);
        closeCreateModal();
    }, [closeCreateModal, navigate]);
    const hasNoResults = !isLoading && dashboards.length === 0;
    const hasActiveSearch = searchValue.trim().length > 0;
    const hasActiveFilters = Boolean(statusFilter);
    const showEmptyState = hasNoResults && !showArchived && !hasActiveSearch && !hasActiveFilters;
    const headerOptions = showEmptyState
        ? []
        : [
            {
                name: translate('Create dashboard'),
                onClick: openCreateModal,
                variant: 'primary',
                disabled: isLoading,
            },
        ];
    if (showEmptyState) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: translate('Dashboards'), subtitle: translate('Create and manage dashboards to visualize multiple charts together.'), LinkComponent: Link }), _jsxs("div", { className: 'flex flex-col items-center justify-center border border-neutral-200 rounded-lg py-16', children: [_jsx(Text, { size: 'sm', className: 'text-neutral-500 mb-4', children: translate('No dashboard created.') }), _jsx(Button, { label: translate('Create dashboard'), variant: 'primary', onClick: openCreateModal })] }), isCreateModalOpen && _jsx(CreateDashboardModal, { onClose: closeCreateModal, onSuccess: handleCreateSuccess })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: translate('Dashboards'), subtitle: translate('Create and manage dashboards to visualize multiple charts together.'), LinkComponent: Link, options: headerOptions }), _jsx(Table, { isLoading: isLoading, data: dashboards, columns: dashboardsColumns, labelNoResults: translate('No dashboards found'), useInternalSearch: true, enableSorting: true, manualFiltering: true, onSearch: (value) => debouncedSearchChange(value || ''), onSearchClear: () => debouncedSearchChange(''), onFilter: handleStatusFilterChange, onFilterClear: () => setStatusFilter(undefined), filterFields: statusFilterFields, labelSearchField: translate('Search...'), customHeaderContent: _jsx("div", { className: 'flex gap-4 ml-auto', children: _jsx("div", { className: 'border-border-primary border-1 rounded-lg py-2 px-4 whitespace-nowrap min-w-max', children: _jsx(Toggle, { label: translate('Show archived'), checked: showArchived, onChange: setShowArchived }) }) }) }), isCreateModalOpen && _jsx(CreateDashboardModal, { onClose: closeCreateModal, onSuccess: handleCreateSuccess })] }));
};
